import type { BuildingGraphPlanarTopologyHandle } from "building-types/entities/buildingEntityHandle";
import type { GeneratedEntityHandle } from "building-types/handles/generatedEntityHandleTypes";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { planarTopologyHandleWithDatumBounds, type PlanarTopologySplitRegion } from "./planarTopologySplitRegions";

/** The generated entity a click on a planar topology split region selects. */
export function pickHandleForPlanarTopologySplitRegion(
  topologyId: string,
  splitRegion: PlanarTopologySplitRegion,
  entityHandle: BuildingGraphPlanarTopologyHandle = planarTopologyHandleWithDatumBounds(topologyId, splitRegion),
): GeneratedEntityHandle {
  return { type: "GENERATED_ENTITY", entity: entityHandle };
}

function axisDistanceToRange(value: number, rangeMin: number, rangeMax: number): number {
  if (value < rangeMin) {
    return rangeMin - value;
  }
  if (value > rangeMax) {
    return value - rangeMax;
  }
  return 0;
}

/** Pick the split region containing `(topologyU, topologyV)`, or the nearest region by UV distance. */
export function pickSplitRegionForTopologyUV(
  splitRegions: readonly PlanarTopologySplitRegion[],
  topologyU: number,
  topologyV: number,
): PlanarTopologySplitRegion {
  let bestRegion = splitRegions[0];
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const region of splitRegions) {
    const containsU = topologyU >= region.uMin && topologyU <= region.uMax;
    const containsV = topologyV >= region.vMin && topologyV <= region.vMax;
    if (containsU && containsV) {
      return region;
    }

    const distance =
      axisDistanceToRange(topologyU, region.uMin, region.uMax) +
      axisDistanceToRange(topologyV, region.vMin, region.vMax);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestRegion = region;
    }
  }

  assertIsNotNullOrUndefined(bestRegion);
  return bestRegion;
}
